import React, { useState } from 'react';
import './LoginPage.css';
import { apiConfig, apiCall } from '../config/api';

const LoginPage = ({ onLoginSuccess, onSwitchToSignup, onForgotPassword }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: true
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    
    if (!formData.password) newErrors.password = 'Password is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    
    try {
      const response = await apiCall(apiConfig.endpoints.auth.login, {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Tokens are now stored in httpOnly cookies by the server
        // Just store user data in localStorage for UI purposes only
        localStorage.setItem('aminole_user', JSON.stringify(data.user));
        
        // Store user data for class system lookup
        const allUsers = JSON.parse(localStorage.getItem('all_users') || '[]');
        const existingUserIndex = allUsers.findIndex(u => u.id === data.user.id);
        if (existingUserIndex >= 0) {
          allUsers[existingUserIndex] = data.user;
        } else {
          allUsers.push(data.user);
        }
        localStorage.setItem('all_users', JSON.stringify(allUsers));
        
        // Call success callback
        if (onLoginSuccess) {
          onLoginSuccess(data.user);
        }
      } else {
        // Handle server errors
        if (data.errors) {
          const serverErrors = {};
          data.errors.forEach(error => {
            serverErrors[error.param] = error.msg;
          });
          setErrors(serverErrors);
        } else {
          setErrors({ general: data.error || 'Login failed' });
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: 'Network error. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img 
            src={`${process.env.PUBLIC_URL}/assets/images/Aminole-Logo.png`} 
            alt="Aminole Logo" 
            className="login-logo" 
          />
          <h2>Welcome Back</h2>
          <p>Sign in to continue learning amino acids</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {errors.general && (
            <div className="error-message general-error">
              {errors.general}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              placeholder="Enter your email address"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={errors.password ? 'error' : ''}
              placeholder="Enter your password"
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group remember-me-group">
            <label className="checkbox-label" style={{visibility: 'hidden'}}>
              <input
                type="checkbox"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleInputChange}
                className="remember-checkbox"
                disabled
              />
              <span className="checkmark"></span>
              Sessions managed securely
            </label>
            {onForgotPassword && (
              <button 
                type="button" 
                className="forgot-password-link"
                onClick={onForgotPassword}
              >
                Forgot Password?
              </button>
            )}
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Don't have an account?{' '}
            <button 
              type="button" 
              className="link-button"
              onClick={onSwitchToSignup}
            >
              Sign Up
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
